import * as Sentry from "@sentry/capacitor";
import { isNetworkError, nativeErrorData } from "./networkError";

export type ActionErrorKey =
  | "networkError"
  | "technicalError"
  | "fileNotReadableError"
  | "emptyFileError";

/**
 * The driver picked an empty (0-byte) file. It's refused before upload: it
 * has nothing to show, and the preview worker can't render it (DORA-478).
 */
export class EmptyFileError extends Error {
  constructor() {
    super("The file is empty");
    this.name = "EmptyFileError";
  }
}

/**
 * The browser lost read access to a file the driver picked (e.g. an Android
 * content-URI or camera file revoked/moved after selection). Picking the file
 * again fixes it, so it's not a bug to report. Matches the raw DOMException
 * and the plain object createAsyncThunk serializes it to.
 */
export const isFileNotReadableError = (err: unknown): boolean =>
  err !== null &&
  typeof err === "object" &&
  (err as Record<string, unknown>).name === "NotReadableError";

/**
 * createAsyncThunk rejects with a plain serialized object (e.g. a Capacitor
 * native error `{code, message, name, stack}`), which Sentry records as
 * "Object captured as promise rejection" with no message. Rebuild a real Error
 * so the report carries the original message and code.
 */
export const toError = (err: unknown): Error => {
  if (err instanceof Error) {
    // A raw Capacitor plugin error keeps its code in `code`, not the message;
    // fold it in so it reports and groups the same as the serialized form.
    const code = (err as Error & { code?: unknown }).code;
    if (
      nativeErrorData(err) &&
      typeof code === "string" &&
      !err.message.startsWith(code)
    ) {
      const error = new Error(`${code}: ${err.message}`);
      error.name = err.name;
      error.stack = err.stack;
      return error;
    }
    return err;
  }
  if (err !== null && typeof err === "object") {
    const e = err as Record<string, unknown>;
    const message = typeof e.message === "string" ? e.message : JSON.stringify(e);
    const error = new Error(
      typeof e.code === "string" ? `${e.code}: ${message}` : message
    );
    if (typeof e.name === "string") error.name = e.name;
    if (typeof e.stack === "string") error.stack = e.stack;
    return error;
  }
  return new Error(String(err));
};

/**
 * The native details of a Capacitor plugin error worth reporting. Leaves out
 * `source`/`target` (the file-transfer ones hold the presigned S3 URL) and
 * response headers.
 */
const nativeErrorExtra = (err: unknown): Record<string, unknown> => {
  const data = nativeErrorData(err);
  if (!data) return {};
  const extra: Record<string, unknown> = {};
  if (data.exception !== undefined) extra.nativeException = data.exception;
  if (data.httpStatus !== undefined) extra.nativeHttpStatus = data.httpStatus;
  if (typeof data.body === "string") {
    extra.nativeResponseBody = data.body.slice(0, 1000);
  }
  return extra;
};

/**
 * Handles a failed driver action: connectivity failures are expected on mobile
 * and only shown to the driver, anything else is also reported to Sentry.
 * Returns the translation key (in the "translation" namespace) to display.
 */
export const handleActionError = (
  err: unknown,
  extra?: Record<string, unknown>
): ActionErrorKey => {
  if (isNetworkError(err)) {
    return "networkError";
  }
  if (isFileNotReadableError(err)) {
    return "fileNotReadableError";
  }
  if (err instanceof EmptyFileError) {
    return "emptyFileError";
  }
  const fullExtra = { ...extra, ...nativeErrorExtra(err) };
  Sentry.captureException(
    toError(err),
    Object.keys(fullExtra).length ? { extra: fullExtra } : undefined
  );
  return "technicalError";
};
